import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bluetooth, BookOpen, Check, CheckCheck, Crown, Download, Eye, FileJson, FilePlus2, FlipHorizontal2, Grid3X3, Hash, ImageDown, ImagePlus, Images, Maximize2, Moon, MoreHorizontal, Palette, PanelLeftClose, PanelRightClose, PencilRuler, Printer, ReplaceAll, Ruler, Scaling, ScanBarcode, ScanSearch, Send, ShieldCheck, ShoppingBag, SlidersHorizontal, Sparkles, Stamp, Sun, SwatchBook, TableProperties, Trash2, Upload } from 'lucide-react';
import { useAuth } from './auth/AuthContext';
import { AccountMenu } from './components/AccountMenu';
import { AuthModal } from './components/AuthModal';
import { BeadCanvas } from './components/BeadCanvas';
import { CanvasResizeModal, type ResizeAnchor } from './components/CanvasResizeModal';
import { ColorReplaceModal } from './components/ColorReplaceModal';
import { ColorCalibrationModal } from './components/ColorCalibrationModal';
import { ColorPanel } from './components/ColorPanel';
import { ContentCenterPage } from './components/ContentCenterPage';
import { GalleryPage } from './components/GalleryPage';
import { ImageCropModal } from './components/ImageCropModal';
import { MembershipModal } from './components/MembershipModal';
import { PatternRecognitionModal } from './components/PatternRecognitionModal';
import { PaletteConversionModal } from './components/PaletteConversionModal';
import { PatternLineAdjustModal } from './components/PatternLineAdjustModal';
import { PublishPatternModal } from './components/PublishPatternModal';
import { ProductsPage } from './components/ProductsPage';
import { PrintPatternModal } from './components/PrintPatternModal';
import { QuickPaletteModal } from './components/QuickPaletteModal';
import { DEFAULT_MOUSE_BUTTONS, EditorSettingsModal } from './components/EditorSettingsModal';
import { useSiteCopy } from './content/SiteCopyContext';
import { siteAssetBackground, useSiteAsset } from './content/SiteAssetsContext';
import { ProjectPanel } from './components/ProjectPanel';
import { SetupPanel } from './components/SetupPanel';
import { SmartBoardPanel } from './components/SmartBoardPanel';
import { ShortcutPaletteModal } from './components/ShortcutPaletteModal';
import { SharePatternScannerModal } from './components/SharePatternScannerModal';
import { WatermarkModal } from './components/WatermarkModal';
import { OperationPage } from './components/OperationPage';
import { FeedbackDialog } from './components/FeedbackDialog';
import { SeoManager } from './components/SeoManager';
import { useExperience } from './content/ExperienceContext';
import { exportPatternPng } from './core/exportPattern';
import { adjustPatternLine, type PatternAxis, type PatternLineAction } from './core/adjustPatternLine';
import { fillContiguousArea } from './core/fillPattern';
import { MARD_291 } from './core/palette';
import { MIXED_COLOR, replacePatternColor, type PatternColorChoice, type PatternColorTarget, type PatternReplaceScope } from './core/replacePatternColor';
import { exportProjectFile, importProjectFile } from './core/projectFile';
import { watermarkFromDocument } from './core/watermark';
import { processImage } from './core/processor';
import { smartBoard } from './core/smartBoard';
import { deleteProject, deleteProjects, listProjects, saveProject } from './storage/projects';
import { queueCloudProject, removeCloudProject, removeCloudProjects, syncCloudProjects } from './sync/projects';
import type { BlankCanvasOptions, CanvasSelection, CanvasTool, FocusMode, GridMode, MembershipEntitlements, MembershipPlan, MouseButtonMap, PatternProject, PatternWatermark, ProcessOptions, ProcessResult } from './types';

const FREE_ACCESS: MembershipEntitlements = { maxCanvasSize: 156, allowBlankCanvas: false, allowAdvancedEdit: false, allowLosslessFiles: false };
const PRO_ACCESS: MembershipEntitlements = { maxCanvasSize: 512, allowBlankCanvas: true, allowAdvancedEdit: true, allowLosslessFiles: true };
type AppScreen = 'workspace' | 'gallery' | 'palettes' | 'tutorials' | 'products' | 'operation';
const ProductPageMotion = lazy(() => import('./components/ProductPageMotion').then((module) => ({ default: module.ProductPageMotion })));

function screenFromHash(): AppScreen {
  const value = window.location.hash.replace('#', '');
  if (value.startsWith('operation/')) return 'operation';
  return value === 'workspace' || value === 'palettes' || value === 'tutorials' || value === 'products' ? value : 'gallery';
}

interface PatternClipboard {
  width: number;
  height: number;
  cells: Array<{ col: number; row: number; colorIndex: number }>;
}

function mostCommonColor(project: PatternProject) {
  const counts = new Map<number, number>();
  for (let i = 0; i < project.colorIndices.length; i += 1) {
    if (!project.empty[i]) counts.set(project.colorIndices[i], (counts.get(project.colorIndices[i]) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function createProjectName(filename: string) {
  const base = filename.replace(/\.[^.]+$/, '').trim();
  return (base || '未命名图纸').slice(0, 40);
}

function cloneProject(project: PatternProject): PatternProject {
  return { ...project, colorIndices: project.colorIndices.slice(), empty: project.empty.slice(), completed: project.completed.slice() };
}

function upsertProjectList(projects: PatternProject[], project: PatternProject) {
  return [project, ...projects.filter((item) => item.id !== project.id && item.projectUid !== project.projectUid)]
    .sort((left, right) => right.updatedAt - left.updatedAt);
}

function normalizeShortcutColors(values: unknown) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.filter((value): value is number => Number.isInteger(value) && value >= 0 && value < MARD_291.length))]
    .sort((left, right) => left - right)
    .slice(0, 36);
}

function isMobileTouchDevice() {
  const navigatorWithHints = navigator as Navigator & { userAgentData?: { mobile?: boolean; platform?: string } };
  const userAgent = navigator.userAgent || '';
  const mobileUserAgent = /Android|iPad|iPhone|iPod|Mobile|Tablet|Silk|Kindle/i.test(userAgent);
  const ipadDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
  return hasTouch && (navigatorWithHints.userAgentData?.mobile === true || mobileUserAgent || ipadDesktopMode);
}

function readAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('无法读取图片'));
    reader.readAsDataURL(file);
  });
}

export default function App() {
  const { user, loading: authLoading, refreshUser, updateEditorPreferences } = useAuth();
  const copy = useSiteCopy();
  const { locale, setLocale, theme, setTheme, lightPalette, setLightPalette, languagesEnabled, darkModeEnabled, label } = useExperience();
  const emptyWorkspaceBackground = useSiteAsset('workspace.emptyBackground');
  const ownerKey = user?.id || '';
  const activeRef = useRef<PatternProject | null>(null);
  const importInput = useRef<HTMLInputElement>(null);
  const backgroundInput = useRef<HTMLInputElement>(null);
  const headerMoreRef = useRef<HTMLDivElement>(null);
  const undoStack = useRef<PatternProject[]>([]);
  const redoStack = useRef<PatternProject[]>([]);
  const [projects, setProjects] = useState<PatternProject[]>([]);
  const [activeProject, setActiveProject] = useState<PatternProject | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [focusMode, setFocusMode] = useState<FocusMode>('all');
  const [tool, setTool] = useState<CanvasTool>('pan');
  const [gridMode, setGridMode] = useState<GridMode>('fine');
  const [showCodes, setShowCodes] = useState(true);
  const [showMarks, setShowMarks] = useState(true);
  const [showHighlight, setShowHighlight] = useState(true);
  const [beadBoardMode, setBeadBoardMode] = useState(false);
  const [eyeComfortMode, setEyeComfortMode] = useState(() => window.localStorage.getItem('yxmpd-eye-comfort-v1') === 'on');
  const [editDimPercent, setEditDimPercent] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipAccess, setMembershipAccess] = useState<MembershipEntitlements>(FREE_ACCESS);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [railOpen, setRailOpen] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [headerMoreOpen, setHeaderMoreOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(true);
  const [quickPaletteOpen, setQuickPaletteOpen] = useState(false);
  const [shortcutPaletteOpen, setShortcutPaletteOpen] = useState(false);
  const [shortcutPaletteColors, setShortcutPaletteColors] = useState<number[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [pendingBackgroundCrop, setPendingBackgroundCrop] = useState<File | null>(null);
  const [pendingPatternRecognition, setPendingPatternRecognition] = useState<File | null>(null);
  const [pendingColorCalibration, setPendingColorCalibration] = useState<{
    kind: 'image' | 'recognized';
    result: ProcessResult;
    file: File;
    options?: ProcessOptions;
    sourceImage?: string;
  } | null>(null);
  const [adjustmentProject, setAdjustmentProject] = useState<PatternProject | null>(null);
  const [screen, setScreen] = useState<AppScreen>(screenFromHash);
  const [operationSlug, setOperationSlug] = useState(() => decodeURIComponent(window.location.hash.replace(/^#operation\//, '')));
  const [pendingGalleryPatternId, setPendingGalleryPatternId] = useState(() => new URLSearchParams(window.location.search).get('pattern') || '');
  const [publishOpen, setPublishOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [selection, setSelection] = useState<CanvasSelection | null>(null);
  const [patternClipboard, setPatternClipboard] = useState<PatternClipboard | null>(null);
  const [hoverCell, setHoverCell] = useState<{ col: number; row: number } | null>(null);
  const [pastePlacement, setPastePlacement] = useState(false);
  const [resizeOpen, setResizeOpen] = useState(false);
  const [lineAdjustOpen, setLineAdjustOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [mouseSettingsOpen, setMouseSettingsOpen] = useState(false);
  const [mouseSettingsSaving, setMouseSettingsSaving] = useState(false);
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [deviceConnected, setDeviceConnected] = useState(() => smartBoard.getStatus().connected);
  const [mobileTouchDevice] = useState(isMobileTouchDevice);
  const [tabletMode, setTabletMode] = useState(false);
  const [tabletOrientation, setTabletOrientation] = useState<'portrait' | 'landscape'>(() => window.innerHeight > window.innerWidth ? 'portrait' : 'landscape');
  const [pendingWorkspace, setPendingWorkspace] = useState(false);
  const [shareScannerOpen, setShareScannerOpen] = useState(false);
  const [watermarkOpen, setWatermarkOpen] = useState(false);
  const [paletteConversionOpen, setPaletteConversionOpen] = useState(false);
  const [watermark, setWatermark] = useState<PatternWatermark | null>(null);

  const mouseButtons: MouseButtonMap = user?.editorMouseSettings || DEFAULT_MOUSE_BUTTONS;
  const canAccessHardwareBeta = Boolean(user?.permissions?.canAccessHardwareBeta);
  const canUseEditor = membershipAccess.allowAdvancedEdit || Boolean(user?.permissions?.canUploadGallery);
  const shortcutPaletteStorageKey = ownerKey && activeProject
    ? `yxmpd-shortcut-palette-v2:${ownerKey}:${activeProject.projectUid || activeProject.id}`
    : '';
  const watermarkStorageKey = ownerKey && activeProject
    ? `yxmpd-watermark-v2:${ownerKey}:${activeProject.projectUid || activeProject.id}`
    : '';

  useEffect(() => { activeRef.current = activeProject; }, [activeProject]);

  useEffect(() => { window.localStorage.setItem('yxmpd-eye-comfort-v1', eyeComfortMode ? 'on' : 'off'); }, [eyeComfortMode]);

  useEffect(() => { setSelection(null); setHoverCell(null); setPastePlacement(false); }, [activeProject?.id]);

  useEffect(() => { setPatternClipboard(null); }, [ownerKey]);

  useEffect(() => {
    if (!headerMoreOpen) return;
    function closeHeaderMore(event: PointerEvent | KeyboardEvent) {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      if (event instanceof PointerEvent && headerMoreRef.current?.contains(event.target as Node)) return;
      setHeaderMoreOpen(false);
      setExportOpen(false);
    }
    document.addEventListener('pointerdown', closeHeaderMore);
    document.addEventListener('keydown', closeHeaderMore);
    return () => {
      document.removeEventListener('pointerdown', closeHeaderMore);
      document.removeEventListener('keydown', closeHeaderMore);
    };
  }, [headerMoreOpen]);

  useEffect(() => {
    if (!shortcutPaletteStorageKey) { setShortcutPaletteColors([]); return; }
    try {
      setShortcutPaletteColors(normalizeShortcutColors(JSON.parse(window.localStorage.getItem(shortcutPaletteStorageKey) || '[]')));
    } catch { setShortcutPaletteColors([]); }
  }, [shortcutPaletteStorageKey]);

  useEffect(() => {
    if (!watermarkStorageKey) { setWatermark(null); return; }
    const controller = new AbortController();
    try {
      const stored = JSON.parse(window.localStorage.getItem(watermarkStorageKey) || 'null');
      if (!stored?.id || !Number.isFinite(stored.x) || !Number.isFinite(stored.y) || !user?.permissions.canUploadGallery) {
        setWatermark(null);
        return () => controller.abort();
      }
      fetch(`/api/watermarks/${encodeURIComponent(stored.id)}`, { credentials: 'include', signal: controller.signal })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.message || '水印读取失败');
          setWatermark(watermarkFromDocument(data.watermark.id, data.watermark.name, data.watermark.document, stored.x, stored.y, stored.scale, stored.locked !== false));
        })
        .catch((reason) => { if (reason.name !== 'AbortError') setWatermark(null); });
    } catch { setWatermark(null); }
    return () => controller.abort();
  }, [user?.permissions.canUploadGallery, watermarkStorageKey]);

  function saveWatermark(next: PatternWatermark | null) {
    setWatermark(next);
    if (!watermarkStorageKey) return;
    if (next) window.localStorage.setItem(watermarkStorageKey, JSON.stringify({ id: next.id, x: next.x, y: next.y, scale: next.scale, locked: next.locked }));
    else window.localStorage.removeItem(watermarkStorageKey);
  }

  function openWatermarkForEdit(project: PatternProject) {
    void saveProject(project).catch(() => setError('水印图纸保存到本地失败'));
    setProjects((current) => upsertProjectList(current, project));
    openProject(project);
    enterEditMode(mostCommonColor(project));
    setNotice(`已打开水印图纸“${project.name}”，修改后再次点击水印并保存当前修改`);
  }

  function saveShortcutPalette(colors: number[]) {
    const next = normalizeShortcutColors(colors);
    setShortcutPaletteColors(next);
    if (shortcutPaletteStorageKey) window.localStorage.setItem(shortcutPaletteStorageKey, JSON.stringify(next));
  }

  useEffect(() => {
    const tabletWidth = window.matchMedia('(min-width: 700px) and (max-width: 1600px)');
    const portrait = window.matchMedia('(orientation: portrait)');
    const forced = new URLSearchParams(window.location.search).get('tablet') === '1';
    const detect = () => {
      setTabletMode(forced || (mobileTouchDevice && tabletWidth.matches));
      setTabletOrientation(portrait.matches ? 'portrait' : 'landscape');
    };
    detect();
    tabletWidth.addEventListener('change', detect);
    portrait.addEventListener('change', detect);
    window.addEventListener('resize', detect);
    return () => {
      tabletWidth.removeEventListener('change', detect);
      portrait.removeEventListener('change', detect);
      window.removeEventListener('resize', detect);
    };
  }, [mobileTouchDevice]);

  useEffect(() => {
    if (!tabletMode || !activeProject) return;
    setRailOpen(false);
    if (tabletOrientation === 'portrait') setColorOpen(false);
  }, [activeProject?.id, tabletMode, tabletOrientation]);

  useEffect(() => {
    if (authLoading) return;
    if (!user && screen === 'workspace') {
      setScreen('gallery');
      window.history.replaceState(null, '', '#gallery');
    } else if (user && pendingWorkspace) {
      setPendingWorkspace(false);
      setScreen('workspace');
      window.history.replaceState(null, '', '#workspace');
    }
  }, [authLoading, pendingWorkspace, screen, user]);

  useEffect(() => {
    const syncHash = () => {
      const next = screenFromHash();
      setScreen(next);
      if (next === 'operation') setOperationSlug(decodeURIComponent(window.location.hash.replace(/^#operation\//, '')));
    };
    window.addEventListener('hashchange', syncHash);
    return () => window.removeEventListener('hashchange', syncHash);
  }, []);

  useEffect(() => {
    if (!canAccessHardwareBeta || !deviceConnected || !activeProject) return;
    void smartBoard.sendFocus(activeProject, selectedColor, focusMode).catch(() => setDeviceConnected(false));
  }, [activeProject?.id, canAccessHardwareBeta, deviceConnected, focusMode, selectedColor]);

  useEffect(() => {
    if (canAccessHardwareBeta) return;
    setDeviceConnected(false);
    if (smartBoard.getStatus().connected) void smartBoard.disconnect();
  }, [canAccessHardwareBeta]);

  useEffect(() => {
    if (!user) { setMembershipAccess(FREE_ACCESS); return; }
    setMembershipAccess(user.tier === 'free' ? FREE_ACCESS : PRO_ACCESS);
    const controller = new AbortController();
    fetch(`/api/membership/plans${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`, { credentials: 'include', signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || '会员权益读取失败');
        const plan = (data.plans as MembershipPlan[] | undefined)?.find((item) => item.tier === user.tier);
        if (plan?.entitlements) setMembershipAccess(plan.entitlements);
      })
      .catch((reason) => { if (reason.name !== 'AbortError') setError('会员权益读取失败，已使用默认权限'); });
    return () => controller.abort();
  }, [locale, user]);

  useEffect(() => {
    if (!canUseEditor && editMode) {
      setEditMode(false);
      setTool('pan');
    }
  }, [canUseEditor, editMode]);

  useEffect(() => {
    if (editMode) setRailOpen(false);
  }, [editMode]);

  const refreshProjects = useCallback(async (preferred?: PatternProject | null) => {
    if (!ownerKey) {
      setProjects([]);
      setActiveProject(null);
      setSelectedColor(null);
      activeRef.current = null;
      return;
    }
    let nextPreferred = preferred || null;
    if (nextPreferred?.ownerKey === 'guest') {
      nextPreferred = { ...nextPreferred, ownerKey, updatedAt: Date.now() };
      await saveProject(nextPreferred);
    }
    const migrationKey = `yxmpd-guest-claimed:${ownerKey}`;
    if (!window.localStorage.getItem(migrationKey)) {
      const guestProjects = await listProjects('guest');
      const ownedProjects = await listProjects(ownerKey);
      const ownedUids = new Set(ownedProjects.map((project) => project.projectUid));
      for (const project of guestProjects) {
        if (ownedUids.has(project.projectUid)) continue;
        await saveProject({ ...project, ownerKey, updatedAt: Date.now() });
        ownedUids.add(project.projectUid);
      }
      window.localStorage.setItem(migrationKey, '1');
    }
    let list = await listProjects(ownerKey);
    try { list = await syncCloudProjects(ownerKey); }
    catch { setNotice('当前使用本地项目，云端同步稍后会自动重试'); }
    setProjects(list);
    const refreshedPreferred = nextPreferred?.ownerKey === ownerKey
      ? list.find((project) => project.id === nextPreferred.id || project.projectUid === nextPreferred.projectUid)
      : null;
    const next = refreshedPreferred || list[0] || null;
    const projectChanged = activeRef.current?.id !== next?.id;
    setActiveProject(next);
    activeRef.current = next;
    if (projectChanged) setSelectedColor(next ? mostCommonColor(next) : null);
  }, [ownerKey]);

  useEffect(() => {
    refreshProjects(activeRef.current).catch(() => setError('无法读取本地项目'));
  }, [refreshProjects]);

  useEffect(() => {
    if (!activeProject) return;
    const timer = window.setTimeout(() => {
      saveProject(activeProject).then(() => {
        queueCloudProject(activeProject);
        setProjects((current) => upsertProjectList(current, activeProject));
      }).catch(() => setError('自动保存失败，请检查浏览器存储权限'));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [activeProject]);

  useEffect(() => {
    if (!error && !notice) return;
    const timer = window.setTimeout(() => { setError(''); setNotice(''); }, 3500);
    return () => window.clearTimeout(timer);
  }, [error, notice]);

  async function generate(file: File, options: ProcessOptions) {
    if (options.width > membershipAccess.maxCanvasSize || options.height > membershipAccess.maxCanvasSize) {
      openUpgrade(`超过 ${membershipAccess.maxCanvasSize} 格的大画布`);
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const [result, sourceImage] = await Promise.all([processImage(file, options), readAsDataUrl(file)]);
      setPendingColorCalibration({ kind: 'image', result, file, options: { ...options }, sourceImage });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '图纸生成失败');
    } finally {
      setGenerating(false);
    }
  }

  async function acceptGeneratedPattern(result: ProcessResult, pending: NonNullable<typeof pendingColorCalibration>) {
    const { file, options, sourceImage } = pending;
    const now = Date.now();
    const project: PatternProject = adjustmentProject ? {
      ...adjustmentProject,
      width: result.width,
      height: result.height,
      colorIndices: result.colorIndices,
      empty: result.empty,
      completed: new Uint8Array(result.width * result.height),
      updatedAt: now,
      sourceName: file.name,
      elapsedMs: result.elapsedMs,
      sourceImage,
      processOptions: options ? { ...options } : undefined
    } : {
      id: crypto.randomUUID(),
      projectUid: `PRJ-${crypto.randomUUID()}`,
      ownerKey,
      name: createProjectName(file.name),
      width: result.width,
      height: result.height,
      colorIndices: result.colorIndices,
      empty: result.empty,
      completed: new Uint8Array(result.width * result.height),
      createdAt: now,
      updatedAt: now,
      sourceName: file.name,
      elapsedMs: result.elapsedMs,
      sourceImage,
      processOptions: options ? { ...options } : undefined
    };
    await saveProject(project);
    setActiveProject(project);
    activeRef.current = project;
    setSelectedColor(mostCommonColor(project));
    setProjects((current) => upsertProjectList(current, project));
    setAdjustmentProject(null);
    setPendingColorCalibration(null);
    setFocusMode('all');
    setEditMode(false);
    setTool('pan');
    undoStack.current = [];
    redoStack.current = [];
    setHistoryVersion((value) => value + 1);
    setNotice(`${adjustmentProject ? '已重新生成' : '已在本地生成'} ${project.width} × ${project.height} 图纸${result.warning ? ` · ${result.warning}` : ''}`);
  }

  async function acceptRecognizedPattern(result: ProcessResult, file: File, recognitionShortcutColors: number[] = []) {
    if (!file) return;
    const now = Date.now();
    const project: PatternProject = {
      id: crypto.randomUUID(),
      projectUid: `PRJ-${crypto.randomUUID()}`,
      ownerKey,
      name: createProjectName(file.name),
      width: result.width,
      height: result.height,
      colorIndices: result.colorIndices,
      empty: result.empty,
      completed: new Uint8Array(result.width * result.height),
      createdAt: now,
      updatedAt: now,
      sourceName: `${file.name}（成品图纸识别）`,
      elapsedMs: result.elapsedMs
    };
    try {
      await saveProject(project);
      setProjects((current) => upsertProjectList(current, project));
      setActiveProject(project);
      activeRef.current = project;
      const normalizedShortcutColors = normalizeShortcutColors(recognitionShortcutColors);
      setShortcutPaletteColors(normalizedShortcutColors);
      if (normalizedShortcutColors.length) {
        const projectPaletteKey = `yxmpd-shortcut-palette-v2:${ownerKey}:${project.projectUid || project.id}`;
        window.localStorage.setItem(projectPaletteKey, JSON.stringify(normalizedShortcutColors));
      }
      setSelectedColor(mostCommonColor(project));
      setFocusMode('all');
      setEditMode(false);
      setTool('pan');
      undoStack.current = [];
      redoStack.current = [];
      setHistoryVersion((value) => value + 1);
      setPendingColorCalibration(null);
      const beadCount = result.empty.reduce((sum, value) => sum + (value ? 0 : 1), 0);
      setNotice(`已识别并导入 ${project.width} × ${project.height} 图纸，共 ${beadCount} 颗`);
    } catch {
      setError('识别结果保存失败，请检查浏览器存储权限');
    }
  }

  function updateProject(updater: (project: PatternProject) => PatternProject) {
    setActiveProject((current) => {
      if (!current) return null;
      const next = updater(current);
      activeRef.current = next;
      return next;
    });
  }

  function openProject(project: PatternProject) {
    setAdjustmentProject(null);
    setActiveProject(project);
    activeRef.current = project;
    setSelectedColor(mostCommonColor(project));
    setFocusMode('all');
    setEditMode(false);
    setTool('pan');
    undoStack.current = [];
    redoStack.current = [];
    setHistoryVersion((value) => value + 1);
  }

  async function importGalleryProject(project: PatternProject) {
    await saveProject(project);
    setProjects((current) => upsertProjectList(current, project));
    openProject(project);
    if (user?.permissions.canUploadGallery) {
      enterEditMode(mostCommonColor(project));
      setNotice(`“${project.name}”已加入本地图纸并进入编辑模式`);
    } else {
      setNotice(`“${project.name}”已加入本地图纸`);
    }
  }

  function enterEditMode(preferredColor?: number | null) {
    if (!canUseEditor) {
      openUpgrade('高级编辑与参考底图');
      return;
    }
    setEditMode(true);
    setFocusMode('all');
    setTool('pencil');
    setRailOpen(false);
    if (preferredColor !== undefined) setSelectedColor(preferredColor ?? 0);
    else if (selectedColor === null) setSelectedColor(0);
  }

  function exitEditMode() {
    setEditMode(false);
    setTool('pan');
    setRailOpen(true);
  }

  function switchScreen(next: AppScreen) {
    if (next === 'workspace' && !user) {
      setPendingWorkspace(true);
      setAuthOpen(true);
      return;
    }
    setScreen(next);
    window.history.replaceState(null, '', `#${next}`);
  }

  function openGalleryPattern(patternId: string) {
    setPendingGalleryPatternId(patternId);
    setScreen('gallery');
    window.history.replaceState(null, '', '#gallery');
  }

  async function openPublish() {
    const current = await refreshUser();
    if (!current?.permissions?.canUploadGallery) {
      setNotice(copy('publish.permissionDenied'));
      return;
    }
    setPublishOpen(true);
  }

  function startConversionAdjustment(project: PatternProject) {
    if (!project.sourceImage || !project.processOptions) return;
    setAdjustmentProject(project);
    setActiveProject(null);
    activeRef.current = null;
    setEditMode(false);
    setTool('pan');
  }

  function cancelConversionAdjustment() {
    if (!adjustmentProject) return;
    setActiveProject(adjustmentProject);
    activeRef.current = adjustmentProject;
    setSelectedColor(mostCommonColor(adjustmentProject));
    setAdjustmentProject(null);
  }

  async function createBlankCanvas(options: BlankCanvasOptions) {
    if (!membershipAccess.allowBlankCanvas) { openUpgrade('新建空白画布'); return; }
    const now = Date.now();
    const empty = new Uint8Array(options.width * options.height);
    empty.fill(1);
    const project: PatternProject = {
      id: crypto.randomUUID(),
      projectUid: `PRJ-${crypto.randomUUID()}`,
      ownerKey,
      name: options.name.slice(0, 40),
      width: options.width,
      height: options.height,
      colorIndices: new Uint16Array(options.width * options.height),
      empty,
      completed: new Uint8Array(options.width * options.height),
      createdAt: now,
      updatedAt: now,
      sourceName: options.backgroundImage ? '参考底图' : '空白画布',
      elapsedMs: 0,
      backgroundImage: options.backgroundImage,
      backgroundOpacity: options.backgroundOpacity
    };
    await saveProject(project);
    setProjects((current) => upsertProjectList(current, project));
    setActiveProject(project);
    activeRef.current = project;
    setSelectedColor(Math.max(0, MARD_291.findIndex((color) => color.code === 'H7')));
    setEditMode(true);
    setTool('pencil');
    setFocusMode('all');
    undoStack.current = [];
    redoStack.current = [];
    setHistoryVersion((value) => value + 1);
    setNotice(`已创建 ${project.width} × ${project.height} 空白画布`);
  }

  function beginEditStroke() {
    if (!activeRef.current) return;
    undoStack.current = [...undoStack.current.slice(-19), cloneProject(activeRef.current)];
    redoStack.current = [];
    setHistoryVersion((value) => value + 1);
  }

  function editCell(index: number, mode: 'pencil' | 'eraser') {
    if (!canUseEditor) return;
    updateProject((project) => {
      const colorIndices = project.colorIndices.slice();
      const empty = project.empty.slice();
      const completed = project.completed.slice();
      if (mode === 'eraser') {
        empty[index] = 1;
        completed[index] = 0;
      } else if (selectedColor !== null) {
        colorIndices[index] = selectedColor;
        empty[index] = 0;
        completed[index] = 0;
      }
      return { ...project, colorIndices, empty, completed, updatedAt: Date.now() };
    });
  }

  function fillArea(index: number) {
    if (!canUseEditor || selectedColor === null || !activeRef.current) return;
    const result = fillContiguousArea(activeRef.current, index, selectedColor);
    if (!result.count) return;
    updateProject(() => result.project);
    setNotice(`已填充连续区域，共 ${result.count.toLocaleString('zh-CN')} 格`);
  }

  function undoEdit() {
    const previous = undoStack.current.pop();
    const current = activeRef.current;
    if (!previous || !current) return;
    redoStack.current.push(cloneProject(current));
    const next = { ...previous, updatedAt: Date.now() };
    activeRef.current = next;
    setActiveProject(next);
    setHistoryVersion((value) => value + 1);
  }

  function redoEdit() {
    const snapshot = redoStack.current.pop();
    const current = activeRef.current;
    if (!snapshot || !current) return;
    undoStack.current.push(cloneProject(current));
    const next = { ...snapshot, updatedAt: Date.now() };
    activeRef.current = next;
    setActiveProject(next);
    setHistoryVersion((value) => value + 1);
  }

  function mirrorProject() {
    beginEditStroke();
    updateProject((project) => {
      const colorIndices = new Uint16Array(project.colorIndices.length);
      const empty = new Uint8Array(project.empty.length);
      const completed = new Uint8Array(project.completed.length);
      for (let row = 0; row < project.height; row += 1) {
        for (let col = 0; col < project.width; col += 1) {
          const from = row * project.width + col;
          const to = row * project.width + (project.width - col - 1);
          colorIndices[to] = project.colorIndices[from];
          empty[to] = project.empty[from];
          completed[to] = project.completed[from];
        }
      }
      return { ...project, colorIndices, empty, completed, backgroundMirror: !project.backgroundMirror, updatedAt: Date.now() };
    });
    setNotice('图纸已水平镜像，可用撤销恢复');
  }

  function resizeProject(nextWidth: number, nextHeight: number, anchor: ResizeAnchor) {
    const current = activeRef.current;
    if (!current || nextWidth < 16 || nextHeight < 16 || nextWidth > membershipAccess.maxCanvasSize || nextHeight > membershipAccess.maxCanvasSize) return;
    beginEditStroke();
    updateProject((project) => {
      const colorIndices = new Uint16Array(nextWidth * nextHeight);
      const empty = new Uint8Array(nextWidth * nextHeight);
      const completed = new Uint8Array(nextWidth * nextHeight);
      empty.fill(1);
      const horizontal = anchor.includes('left') ? 0 : anchor.includes('right') ? nextWidth - project.width : Math.floor((nextWidth - project.width) / 2);
      const vertical = anchor.startsWith('top') ? 0 : anchor.startsWith('bottom') ? nextHeight - project.height : Math.floor((nextHeight - project.height) / 2);
      for (let row = 0; row < project.height; row += 1) for (let col = 0; col < project.width; col += 1) {
        const nextCol = col + horizontal;
        const nextRow = row + vertical;
        if (nextCol < 0 || nextCol >= nextWidth || nextRow < 0 || nextRow >= nextHeight) continue;
        const from = row * project.width + col;
        const to = nextRow * nextWidth + nextCol;
        colorIndices[to] = project.colorIndices[from];
        empty[to] = project.empty[from];
        completed[to] = project.completed[from];
      }
      return { ...project, width: nextWidth, height: nextHeight, colorIndices, empty, completed, updatedAt: Date.now() };
    });
    setSelection(null);
    setResizeOpen(false);
    setNotice(`画布已调整为 ${nextWidth} × ${nextHeight}，可用撤销恢复`);
  }

  function adjustProjectLine(axis: PatternAxis, action: PatternLineAction, index: number, count: number) {
    const current = activeRef.current;
    if (!current) return;
    const currentLength = axis === 'row' ? current.height : current.width;
    if (!Number.isInteger(count) || count < 1 || (action === 'delete' && currentLength - count < 16) || (action === 'insert' && currentLength + count > membershipAccess.maxCanvasSize)) return;
    beginEditStroke();
    const result = adjustPatternLine(current, axis, action, index, count);
    updateProject(() => result.project);
    setSelection(null);
    setHoverCell(null);
    setPastePlacement(false);
    setLineAdjustOpen(false);
    const axisLabel = axis === 'row' ? '行' : '列';
    if (action === 'insert') {
      setNotice(`已从第 ${index + 1} ${axisLabel}起插入 ${count} ${axisLabel}，原内容已顺序平移，可用撤销恢复`);
    } else {
      const detail = result.removedBeads ? `，移除 ${result.removedBeads} 颗豆${result.removedCompleted ? `（${result.removedCompleted} 颗已完成）` : ''}` : '';
      const range = count === 1 ? `第 ${index + 1}` : `第 ${index + 1}-${index + count}`;
      setNotice(`已删除${range} ${axisLabel}${detail}，可用撤销恢复`);
    }
  }

  function replaceColor(source: PatternColorChoice, target: PatternColorTarget, scope: PatternReplaceScope, replaceSelection: CanvasSelection | null) {
    const current = activeRef.current;
    if (!current || source === target) return;
    const result = replacePatternColor(current, source, target, scope, replaceSelection);
    if (!result.changed) return;
    beginEditStroke();
    updateProject(() => result.project);
    if (target !== null && target !== MIXED_COLOR) setSelectedColor(target);
    setReplaceOpen(false);
    if (target === MIXED_COLOR) {
      setNotice(`已将 ${result.changed} ${source === null ? '个空白格' : '颗豆子'}逐颗随机混色（A1-M15），可用撤销恢复`);
    } else if (target === null) {
      setNotice(`已删除 ${result.changed} 颗 ${MARD_291[source ?? 0]?.code || ''}，对应位置已设为空白，可用撤销恢复`);
    } else if (source === null) {
      setNotice(`已将 ${result.changed} 个空白格填充为 ${MARD_291[target]?.code}，可用撤销恢复`);
    } else {
      setNotice(`已将 ${result.changed} 颗 ${MARD_291[source]?.code} 替换为 ${MARD_291[target]?.code}，可用撤销恢复`);
    }
  }

  function copySelection() {
    const project = activeRef.current;
    if (!project || !selection || !editMode) return;
    const left = Math.min(selection.startCol, selection.endCol);
    const right = Math.max(selection.startCol, selection.endCol);
    const top = Math.min(selection.startRow, selection.endRow);
    const bottom = Math.max(selection.startRow, selection.endRow);
    const cells: PatternClipboard['cells'] = [];
    for (let row = top; row <= bottom; row += 1) for (let col = left; col <= right; col += 1) {
      const index = row * project.width + col;
      if (!project.empty[index]) cells.push({ col: col - left, row: row - top, colorIndex: project.colorIndices[index] });
    }
    const next = { width: right - left + 1, height: bottom - top + 1, cells };
    setPatternClipboard(next);
    setNotice(`已复制 ${next.width} × ${next.height} 区域（${cells.length} 颗），可切换图纸后粘贴`);
  }

  function pasteSelection(target = hoverCell) {
    const project = activeRef.current;
    if (!project || !patternClipboard || !editMode) {
      if (!patternClipboard) setNotice('剪贴板为空，请先框选并按 Ctrl+C 复制');
      return;
    }
    if (!target) {
      setPastePlacement(true);
      setSelection(null);
      setTool('select');
      setNotice('请在画布上点击一个格作为粘贴起点，按 Esc 取消');
      return;
    }
    const cells = patternClipboard.cells.filter((cell) => target.col + cell.col < project.width && target.row + cell.row < project.height);
    if (!cells.length) { setNotice('复制区域内没有可粘贴到当前画布的拼豆'); return; }
    beginEditStroke();
    updateProject((current) => {
      const colorIndices = current.colorIndices.slice();
      const empty = current.empty.slice();
      const completed = current.completed.slice();
      for (const cell of cells) {
        const col = target.col + cell.col;
        const row = target.row + cell.row;
        const index = row * current.width + col;
        colorIndices[index] = cell.colorIndex;
        empty[index] = 0;
        completed[index] = 0;
      }
      return { ...current, colorIndices, empty, completed, updatedAt: Date.now() };
    });
    setSelection({
      startCol: target.col,
      startRow: target.row,
      endCol: Math.min(project.width - 1, target.col + patternClipboard.width - 1),
      endRow: Math.min(project.height - 1, target.row + patternClipboard.height - 1)
    });
    setPastePlacement(false);
    setTool('select');
    setNotice(`已从第 ${target.col + 1} 列、第 ${target.row + 1} 行粘贴 ${cells.length} 颗，可拖动框选区域继续移动`);
  }

  function moveSelection(source: CanvasSelection, deltaCol: number, deltaRow: number) {
    const currentProject = activeRef.current;
    if (!currentProject || (!deltaCol && !deltaRow)) return;
    const left = Math.min(source.startCol, source.endCol);
    const right = Math.max(source.startCol, source.endCol);
    const top = Math.min(source.startRow, source.endRow);
    const bottom = Math.max(source.startRow, source.endRow);
    let moved = 0;
    for (let row = top; row <= bottom; row += 1) for (let col = left; col <= right; col += 1) {
      if (!currentProject.empty[row * currentProject.width + col]) moved += 1;
    }
    beginEditStroke();
    updateProject((project) => {
      const originalColors = project.colorIndices;
      const originalEmpty = project.empty;
      const originalCompleted = project.completed;
      const colorIndices = originalColors.slice();
      const empty = originalEmpty.slice();
      const completed = originalCompleted.slice();
      for (let row = top; row <= bottom; row += 1) for (let col = left; col <= right; col += 1) {
        const index = row * project.width + col;
        colorIndices[index] = 0;
        empty[index] = 1;
        completed[index] = 0;
      }
      for (let row = top; row <= bottom; row += 1) for (let col = left; col <= right; col += 1) {
        const from = row * project.width + col;
        const to = (row + deltaRow) * project.width + col + deltaCol;
        colorIndices[to] = originalColors[from];
        empty[to] = originalEmpty[from];
        completed[to] = originalCompleted[from];
      }
      return { ...project, colorIndices, empty, completed, updatedAt: Date.now() };
    });
    setNotice(`已移动框选区域：横向 ${deltaCol > 0 ? '+' : ''}${deltaCol} 格，纵向 ${deltaRow > 0 ? '+' : ''}${deltaRow} 格（${moved} 颗）`);
  }

  function changeTool(next: CanvasTool) {
    setTool(next);
    setPastePlacement(false);
    if (next !== 'select') setSelection(null);
  }

  function cancelSelection() {
    setSelection(null);
    setPastePlacement(false);
    setNotice('已取消框选');
  }

  async function saveMouseButtons(next: MouseButtonMap) {
    setMouseSettingsSaving(true);
    try {
      await updateEditorPreferences(next);
      setMouseSettingsOpen(false);
      setNotice('鼠标按键设置已同步到账号');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '鼠标设置同步失败');
    } finally {
      setMouseSettingsSaving(false);
    }
  }

  async function updateBackground(file: File | null) {
    if (!canUseEditor) { openUpgrade('参考底图与高级编辑'); return; }
    if (!file || !file.type.startsWith('image/')) return;
    const data = await readAsDataUrl(file);
    beginEditStroke();
    updateProject((project) => ({ ...project, backgroundImage: data, backgroundOpacity: project.backgroundOpacity ?? 0.35, updatedAt: Date.now() }));
    setPendingBackgroundCrop(null);
    setNotice('参考底图已更新');
    if (backgroundInput.current) backgroundInput.current.value = '';
  }

  function toggleComplete(index: number, completedValue?: boolean) {
    const current = activeRef.current;
    if (!current) return;
    const nextCompleted = completedValue ?? !Boolean(current.completed[index]);
    if (Boolean(current.completed[index]) === nextCompleted) return;
    updateProject((project) => {
      const completed = project.completed.slice();
      completed[index] = nextCompleted ? 1 : 0;
      return { ...project, completed, updatedAt: Date.now() };
    });
    if (canAccessHardwareBeta && deviceConnected) void smartBoard.sendCell(current, index, nextCompleted).catch(() => setDeviceConnected(false));
  }

  function markColor(colorIndex: number, completedValue: boolean) {
    updateProject((project) => {
      const completed = project.completed.slice();
      for (let i = 0; i < project.colorIndices.length; i += 1) {
        if (!project.empty[i] && project.colorIndices[i] === colorIndex) completed[i] = completedValue ? 1 : 0;
      }
      return { ...project, completed, updatedAt: Date.now() };
    });
    if (activeRef.current && canAccessHardwareBeta && deviceConnected) void smartBoard.sendColorCompletion(activeRef.current, colorIndex, completedValue).catch(() => setDeviceConnected(false));
  }

  function renameProject(name: string) {
    updateProject((project) => ({ ...project, name: name.slice(0, 40), updatedAt: Date.now() }));
  }

  async function removeProject(project: PatternProject) {
    if (!window.confirm(`确定删除“${project.name}”吗？这个操作无法撤销。`)) return;
    await deleteProject(project.id);
    void removeCloudProject(project.projectUid).catch(() => setNotice('本地项目已删除，云端删除稍后重试'));
    const next = projects.filter((item) => item.id !== project.id);
    setProjects(next);
    if (activeProject?.id === project.id) {
      setActiveProject(next[0] || null);
      setSelectedColor(next[0] ? mostCommonColor(next[0]) : null);
    }
  }

  async function removeProjects(projectsToDelete: PatternProject[]) {
    if (!projectsToDelete.length) return false;
    if (!window.confirm(`确定批量删除选中的 ${projectsToDelete.length} 张图纸吗？这个操作无法撤销。`)) return false;
    const ids = new Set(projectsToDelete.map((project) => project.id));
    try {
      await deleteProjects([...ids]);
    } catch {
      setError('批量删除失败，请检查浏览器存储权限');
      return false;
    }
    const next = projects.filter((project) => !ids.has(project.id));
    setProjects(next);
    if (activeProject && ids.has(activeProject.id)) {
      const replacement = next[0] || null;
      setActiveProject(replacement);
      activeRef.current = replacement;
      setSelectedColor(replacement ? mostCommonColor(replacement) : null);
      setEditMode(false);
      setTool('pan');
    }
    void removeCloudProjects(projectsToDelete.map((project) => project.projectUid))
      .catch(() => setNotice(`已从本机删除 ${projectsToDelete.length} 张图纸，云端删除稍后重试`));
    return true;
  }

  async function importCoordinateProject(file: File | null) {
    if (!file) return;
    if (!membershipAccess.allowLosslessFiles) {
      openUpgrade('无损图纸导入');
      if (importInput.current) importInput.current.value = '';
      return;
    }
    setError('');
    try {
      const project = await importProjectFile(file, ownerKey);
      await saveProject(project);
      setProjects((current) => upsertProjectList(current, project));
      setActiveProject(project);
      activeRef.current = project;
      setSelectedColor(mostCommonColor(project));
      setFocusMode('all');
      setEditMode(false);
      setTool('pan');
      undoStack.current = [];
      redoStack.current = [];
      setHistoryVersion((value) => value + 1);
      setNotice(`已导入 ${project.width} × ${project.height} 无损图纸`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '图纸导入失败');
    } finally {
      if (importInput.current) importInput.current.value = '';
    }
  }

  const beadCount = useMemo(() => {
    if (!activeProject) return 0;
    return activeProject.empty.reduce((sum, empty) => sum + (empty ? 0 : 1), 0);
  }, [activeProject]);
  const canUndo = historyVersion >= 0 && undoStack.current.length > 0;
  const canRedo = historyVersion >= 0 && redoStack.current.length > 0;

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (!activeProject || screen !== 'workspace' || event.defaultPrevented || event.repeat || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches('button, a, input, textarea, select, [contenteditable="true"]') || document.querySelector('[role="dialog"]')) return;
      const key = /^Key[A-Z]$/.test(event.code) ? event.code.slice(3).toLowerCase() : event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && editMode && key === 'c') { copySelection(); event.preventDefault(); return; }
      if ((event.ctrlKey || event.metaKey) && editMode && key === 'v') { pasteSelection(); event.preventDefault(); return; }
      if (event.ctrlKey || event.metaKey) return;
      if (key === 'escape' && editMode && (selection || pastePlacement)) {
        cancelSelection();
        event.preventDefault();
        return;
      }
      let handled = true;
      if (key === 'enter') {
        if (editMode) exitEditMode();
        else enterEditMode();
      }
      else if (key === 'x') mirrorProject();
      else if (key === 'h') changeTool('pan');
      else if (key === 'm') changeTool('mark');
      else if (editMode && key === 'b') changeTool('pencil');
      else if (editMode && key === 'f') changeTool('fill');
      else if (editMode && key === 'e') changeTool('eraser');
      else if (editMode && key === 'i') changeTool('picker');
      else if (editMode && key === 's') changeTool('select');
      else if (editMode && key === 'z' && canUndo) undoEdit();
      else if (editMode && key === 'y' && canRedo) redoEdit();
      else if (editMode && key === 'p') setQuickPaletteOpen(true);
      else if (editMode && key === 'q') setShortcutPaletteOpen(true);
      else handled = false;
      if (handled) event.preventDefault();
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [activeProject, canRedo, canUndo, canUseEditor, editMode, hoverCell, pastePlacement, patternClipboard, screen, selectedColor, selection]);

  useEffect(() => {
    if (!editMode) {
      setQuickPaletteOpen(false);
      setShortcutPaletteOpen(false);
      setSelection(null);
      setPastePlacement(false);
      setResizeOpen(false);
      setLineAdjustOpen(false);
      setReplaceOpen(false);
      setMouseSettingsOpen(false);
    }
    if (editMode) setBeadBoardMode(false);
  }, [editMode]);

  function openUpgrade(feature: string) {
    setNotice(`${feature}属于完整版权益，可以使用专业版兑换码，或在账号中开通其他方案。`);
    setMembershipOpen(true);
  }

  return (
    <div className={`app-shell ${tabletMode ? `tablet-mode tablet-${tabletOrientation}` : ''}`}>
      <SeoManager screen={screen} operationSlug={operationSlug} />
      <a className="skip-link" href="#primary-content">跳到主要内容</a>
      <Suspense fallback={null}><ProductPageMotion screen={screen} /></Suspense>
      <header className="app-header">
        <div className="brand">
          <img className="brand-logo" src="/logo.png" alt="" />
          <span><strong>伊小满拼豆</strong><small>逐色施工助手</small></span>
        </div>
        <div className="header-center">
          <div className="header-context">
            {screen === 'workspace' && activeProject ? <>
              <input className="project-title-input" aria-label="图纸名称" title={activeProject.name} value={activeProject.name} onChange={(event) => renameProject(event.target.value)} />
              <span title={`${activeProject.width} × ${activeProject.height} · ${beadCount} 颗 · 本地 ${activeProject.elapsedMs}ms`}>{activeProject.width} × {activeProject.height} · {beadCount} 颗 · 本地 {activeProject.elapsedMs}ms</span>
            </> : <span className="header-promise"><ShieldCheck size={16} />{screen === 'gallery' ? copy('gallery.headerPromise') : screen === 'palettes' ? '色卡资料由后台统一维护' : screen === 'tutorials' ? '教程内容持续更新' : screen === 'products' ? '商品仅展示，购买由合作商家承接' : '原图只在本机处理'}</span>}
          </div>
          <button className="share-scan-nav-button" type="button" onClick={() => setShareScannerOpen(true)}><ScanBarcode size={17} /><span>{label('scan')}</span></button>
          <button className={`draw-nav-button ${screen === 'workspace' ? 'active' : ''}`} type="button" onClick={() => switchScreen('workspace')}><PencilRuler size={17} /><span>{label('draw')}</span></button>
          <nav className="app-nav community-nav" aria-label="内容与社区">
            <button className={screen === 'gallery' ? 'active' : ''} type="button" onClick={() => switchScreen('gallery')}><Images size={17} /><span>{label('gallery')}</span></button>
            <button className={screen === 'palettes' ? 'active' : ''} type="button" onClick={() => switchScreen('palettes')}><Palette size={17} /><span>{label('palettes')}</span></button>
            <button className={screen === 'tutorials' ? 'active' : ''} type="button" onClick={() => switchScreen('tutorials')}><BookOpen size={17} /><span>{label('tutorials')}</span></button>
            <button className={screen === 'products' ? 'active' : ''} type="button" onClick={() => switchScreen('products')}><ShoppingBag size={17} /><span>{label('products')}</span></button>
          </nav>
          <span className="header-center-spacer" aria-hidden="true" />
        </div>
        <div className="header-actions">
          <div ref={headerMoreRef} className={`header-more${headerMoreOpen ? ' open' : ''}`}>
            <button className="header-icon header-more-toggle" type="button" onClick={() => { setHeaderMoreOpen((current) => !current); if (headerMoreOpen) setExportOpen(false); }} aria-label="更多工具" aria-expanded={headerMoreOpen}><MoreHorizontal size={20} /><span>更多</span></button>
            <div className="header-utility-group">
              {languagesEnabled && <label className="experience-language" title="界面语言"><span className="sr-only">界面语言</span><select value={locale} onChange={(event) => setLocale(event.target.value as '' | 'en' | 'ja' | 'ko')}><option value="">中</option><option value="en">EN</option><option value="ja">日</option><option value="ko">한</option></select></label>}
              <button className="header-icon experience-palette" type="button" onClick={() => setLightPalette(lightPalette === 'blue' ? 'warm' : 'blue')} title={lightPalette === 'blue' ? '切换为暖色基调' : '切换为冷色基调'} aria-label={lightPalette === 'blue' ? '切换为暖色基调' : '切换为冷色基调'}><Palette size={18} /><span>{lightPalette === 'blue' ? '冷' : '暖'}</span></button>
              {darkModeEnabled && <button className="header-icon experience-theme" type="button" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} title={theme === 'dark' ? label('light') : label('dark')}>{theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}<span>{theme === 'dark' ? label('light') : label('dark')}</span></button>}
              {screen === 'workspace' && <button className={`header-icon ${membershipAccess.allowLosslessFiles ? '' : 'feature-locked'}`} type="button" onClick={() => membershipAccess.allowLosslessFiles ? importInput.current?.click() : openUpgrade('无损图纸导入')} title="导入无损图纸"><Upload size={19} /><span>导入</span>{!membershipAccess.allowLosslessFiles && <i className="pro-tag">PRO</i>}</button>}
              <input ref={importInput} type="file" accept=".yxpd" hidden onChange={(event) => importCoordinateProject(event.target.files?.[0] || null)} />
              {screen === 'workspace' && activeProject && <div className="export-root">
                <button className="header-icon" type="button" onClick={() => setExportOpen((value) => !value)} aria-expanded={exportOpen} title="导出图纸"><Download size={19} /><span>导出</span></button>
                {exportOpen && <div className="export-menu">
                  <button className={membershipAccess.allowLosslessFiles ? '' : 'feature-locked'} type="button" onClick={() => { if (!membershipAccess.allowLosslessFiles) { setExportOpen(false); openUpgrade('无损图纸导出'); return; } void exportProjectFile(activeProject).then((exportUid) => setNotice(`加密图纸已导出 · ${exportUid}`)).catch((reason) => setError(reason.message)); setExportOpen(false); }}><FileJson size={18} /><span><strong>加密无损图纸</strong><small>.yxpd v2 · 签名、坐标、色号和进度</small></span>{!membershipAccess.allowLosslessFiles && <i className="pro-tag">PRO</i>}</button>
                  <button type="button" onClick={() => { exportPatternPng(activeProject, { authorName: user?.displayName || '作者' }).then(() => setNotice('高清完整图纸已导出')).catch((reason) => setError(reason.message)); setExportOpen(false); }}><ImageDown size={18} /><span><strong>高清完整图纸</strong><small>.png · 品牌水印、作者与完整色卡</small></span></button>
                  {user?.permissions.canUploadGallery && watermark && <button type="button" onClick={() => { exportPatternPng(activeProject, { authorName: user.displayName || '作者', watermark }).then(() => setNotice('带水印高清图纸已导出')).catch((reason) => setError(reason.message)); setExportOpen(false); }}><Stamp size={18} /><span><strong>带水印导出高清图纸</strong><small>.png · 合成独立拼豆水印层</small></span></button>}
                  <button type="button" onClick={() => { setPrintOpen(true); setExportOpen(false); }}><Printer size={18} /><span><strong>A4打印版</strong><small>2.7mm固定格 · 单色/彩色 · 完整色卡</small></span></button>
                </div>}
              </div>}
              {screen === 'workspace' && activeProject && <button className={`header-icon device-header-button ${canAccessHardwareBeta && deviceConnected ? 'connected' : ''}`} type="button" onClick={() => setDeviceOpen(true)} title={canAccessHardwareBeta && deviceConnected ? '智能豆板已连接' : '智能豆板'}><Bluetooth size={19} /><span>{canAccessHardwareBeta && deviceConnected ? '豆板已连接' : '智能豆板'}</span></button>}
            </div>
          </div>
          <button className="header-icon desktop-only" type="button" onClick={() => document.documentElement.requestFullscreen?.()} title="全屏"><Maximize2 size={19} /></button>
          <AccountMenu onOpenAuth={() => setAuthOpen(true)} onOpenMembership={() => setMembershipOpen(true)} onOpenGalleryPattern={openGalleryPattern} />
        </div>
      </header>

      {screen === 'operation' ? <OperationPage slug={operationSlug} />
        : screen === 'gallery' ? <GalleryPage user={user} onImport={importGalleryProject} onOpenWorkspace={() => switchScreen('workspace')} onRequireAuth={() => setAuthOpen(true)} openPatternId={pendingGalleryPatternId} onOpenPatternHandled={() => setPendingGalleryPatternId('')} />
        : screen === 'palettes' ? <ContentCenterPage kind="palette" />
          : screen === 'tutorials' ? <ContentCenterPage kind="tutorial" />
            : screen === 'products' ? <ProductsPage />
            : !activeProject ? (
        <main id="primary-content" className={`empty-workspace ${railOpen ? '' : 'rail-collapsed'}`}>
          {railOpen && <ProjectPanel projects={projects} onOpen={openProject} onNew={() => { setAdjustmentProject(null); setActiveProject(null); }} onDelete={removeProject} onDeleteMany={removeProjects} onCollapse={() => setRailOpen(false)} />}
          {!railOpen && <button className="rail-toggle" type="button" onClick={() => setRailOpen(true)} aria-label="展开项目栏"><PanelLeftClose size={18} /></button>}
          <section className="welcome-stage" style={siteAssetBackground(emptyWorkspaceBackground)}>
            <div className="welcome-copy">
              <span className="welcome-badge"><Sparkles size={15} />大屏打开，照着拼就行</span>
              <h1><span>只看当前色，</span><em>拼起来更轻松。</em></h1>
              <p>选张图片生成图纸。拼的时候可以只显示一种颜色，拼完就打勾，进度会自动保存在这台设备上。</p>
              <div className="feature-pills"><span>291 色 MARD 色卡</span><span>10 格定位辅助线</span><span>图纸存在本机</span></div>
            </div>
            <SetupPanel
              key={adjustmentProject?.id || 'new-project'}
              generating={generating}
              maxCanvasSize={membershipAccess.maxCanvasSize}
              allowBlankCanvas={membershipAccess.allowBlankCanvas}
              initialSource={adjustmentProject?.sourceImage && adjustmentProject.processOptions ? { dataUrl: adjustmentProject.sourceImage, name: adjustmentProject.sourceName, options: adjustmentProject.processOptions } : undefined}
              onGenerate={generate}
              onRecognize={setPendingPatternRecognition}
              onCreateCanvas={createBlankCanvas}
              onRequirePro={openUpgrade}
              onCancelAdjustment={cancelConversionAdjustment}
            />
          </section>
        </main>
      ) : (
        <main id="primary-content" className={`workspace ${railOpen ? '' : 'rail-collapsed'} ${colorOpen && !editMode ? '' : 'color-collapsed'}`}>
          {railOpen && <ProjectPanel projects={projects} activeId={activeProject.id} onOpen={openProject} onNew={() => { setActiveProject(null); setEditMode(false); }} onDelete={removeProject} onDeleteMany={removeProjects} onCollapse={() => setRailOpen(false)} />}
          <section className="canvas-column">
            <div className="canvas-topbar">
              <button className="rail-toggle inline" type="button" onClick={() => setRailOpen((value) => !value)} aria-label={railOpen ? '收起项目栏' : '展开项目栏'}><PanelLeftClose size={18} /></button>
              <div className="canvas-toggles">
                <button className="active grid-mode-button" data-grid-mode={gridMode} type="button" onClick={() => setGridMode((value) => value === 'fine' ? 'guides' : value === 'guides' ? 'blocks5' : value === 'blocks5' ? 'none' : 'fine')} title={`点击切换网格样式 · 当前${gridMode === 'fine' ? '小格＋十格实线' : gridMode === 'guides' ? '小格＋五格虚线＋十格实线' : gridMode === 'blocks5' ? '小格＋五格实线＋十格粗实线' : '无任何线条'}`} aria-label="切换网格样式"><Grid3X3 size={17} /><span className="toolbar-label">网格</span></button>
                <button className={showCodes ? 'active' : ''} type="button" onClick={() => setShowCodes((value) => !value)} title="显示或隐藏色号" aria-label="显示或隐藏色号"><Hash size={17} /><span className="toolbar-label">色号</span></button>
                {!editMode && <button className={showMarks ? 'visibility-active' : ''} type="button" aria-pressed={showMarks} onClick={() => setShowMarks((value) => !value)} title="显示或隐藏已标记格" aria-label="显示或隐藏标记"><CheckCheck size={17} /><span className="toolbar-label">标记</span></button>}
                {!editMode && <button className={beadBoardMode ? 'board-active' : ''} type="button" aria-pressed={beadBoardMode} onClick={() => setBeadBoardMode((value) => !value)} title="豆板模式：每格按 2.6mm 显示；手动缩放会自动退出" aria-label="切换豆板模式，每格 2.6 毫米"><Ruler size={17} /><span className="toolbar-label">豆板</span></button>}
                {!editMode && <button className={eyeComfortMode ? 'eye-active' : ''} type="button" aria-pressed={eyeComfortMode} onClick={() => setEyeComfortMode((value) => !value)} title="降低画布亮度与蓝光感，缓解长时间观看刺眼" aria-label="切换护眼模式"><Moon size={17} /><span className="toolbar-label">护眼</span></button>}
                {editMode && <button className={showHighlight ? 'visibility-active' : ''} type="button" aria-pressed={showHighlight} onClick={() => setShowHighlight((value) => !value)} title="显示或隐藏当前颜色高亮框" aria-label="显示或隐藏高亮"><ScanSearch size={17} /><span className="toolbar-label">高亮</span></button>}
                {editMode && <button className={editDimPercent > 0 ? 'visibility-active' : ''} type="button" aria-pressed={editDimPercent > 0} onClick={() => setEditDimPercent((value) => value === 0 ? 85 : value === 85 ? 50 : value === 50 ? 25 : 0)} title={`点击切换淡化档位 · 当前 ${editDimPercent}%`} aria-label="切换编辑淡化档位"><Eye size={17} /><span className="toolbar-label">淡化{editDimPercent > 0 ? ` ${editDimPercent}%` : ''}</span></button>}
                <button type="button" onClick={mirrorProject} title="水平镜像（X）" aria-label="水平镜像"><FlipHorizontal2 size={17} /><span className="toolbar-label">镜像</span></button>
              </div>
              <button className={`editor-mode-toggle ${editMode ? 'active' : ''} ${canUseEditor ? '' : 'feature-locked'}`} type="button" title={editMode ? '退出编辑（Enter）' : '进入编辑（Enter）'} aria-label={editMode ? '退出编辑' : '进入编辑'} aria-keyshortcuts="Enter" onClick={() => editMode ? exitEditMode() : enterEditMode()}><PencilRuler size={17} /><span>{editMode ? '退出编辑' : '进入编辑'}</span><kbd>Enter</kbd>{!canUseEditor && <Crown className="inline-pro-crown" size={13} />}</button>
              <div className="canvas-top-actions">
                {editMode && <button className={`new-inline publish-inline ${user?.permissions?.canUploadGallery ? '' : 'feature-locked'}`} type="button" onClick={() => void openPublish()} title={user?.permissions?.canUploadGallery ? copy('publish.title') : copy('publish.permissionDenied')} aria-label={copy('publish.toolbarLabel')}><Send size={17} /><span className="toolbar-label">{copy('publish.toolbarLabel')}</span>{!user?.permissions?.canUploadGallery && <Crown className="inline-pro-crown" size={13} />}</button>}
                {editMode && user?.permissions.canUploadGallery && <button className={`new-inline watermark-inline ${watermark ? 'active' : ''}`} type="button" onClick={() => setWatermarkOpen(true)} title="添加、调整或删除独立拼豆水印层" aria-label="拼豆水印层"><Stamp size={17} /><span className="toolbar-label">水印</span></button>}
                {editMode && watermark && !watermark.locked && <button className="new-inline watermark-confirm-inline" type="button" onClick={() => { saveWatermark({ ...watermark, locked: true }); setNotice('水印位置和大小已确认'); }} title="确认水印位置与大小" aria-label="完成水印调整"><Check size={17} /><span className="toolbar-label">完成调整</span></button>}
                {editMode && <button className="new-inline" type="button" onClick={() => backgroundInput.current?.click()} title="设置参考底图" aria-label="设置参考底图"><ImagePlus size={17} /><span className="toolbar-label">底图</span></button>}
                {editMode && <button className="new-inline" type="button" onClick={() => setResizeOpen(true)} title="调整当前画布大小" aria-label="调整当前画布大小"><Scaling size={17} /><span className="toolbar-label">尺寸</span></button>}
                {editMode && <button className="new-inline" type="button" onClick={() => setLineAdjustOpen(true)} title="单独插入或删除行列" aria-label="单独调整行列"><TableProperties size={17} /><span className="toolbar-label">行列</span></button>}
                {editMode && <button className="new-inline" type="button" onClick={() => setReplaceOpen(true)} title="批量更换颜色" aria-label="批量更换颜色"><ReplaceAll size={17} /><span className="toolbar-label">换色</span></button>}
                {activeProject.sourceImage && activeProject.processOptions && <button className="new-inline adjust-conversion" type="button" onClick={() => startConversionAdjustment(activeProject)} title="调整图片转换" aria-label="调整图片转换"><SlidersHorizontal size={17} /><span className="toolbar-label">调整转换</span></button>}
                {!editMode && <button className="new-inline palette-conversion-inline" type="button" onClick={() => setPaletteConversionOpen(true)} title="转换当前图纸的色号品牌" aria-label="转换色卡品牌"><SwatchBook size={17} /><span className="toolbar-label">转换色卡</span></button>}
                <input ref={backgroundInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => { const file = event.target.files?.[0] || null; if (file?.type.startsWith('image/')) setPendingBackgroundCrop(file); }} />
                <button className="new-inline" type="button" onClick={() => { setAdjustmentProject(null); setActiveProject(null); setEditMode(false); }} title="新建图纸" aria-label="新建图纸"><FilePlus2 size={17} /><span className="toolbar-label">新建</span></button>
              </div>
              {!editMode && <button className={`right-rail-toggle ${colorOpen ? '' : 'closed'}`} type="button" onClick={() => setColorOpen((value) => !value)} aria-label={colorOpen ? '收起色号栏' : '展开色号栏'}><PanelRightClose size={18} /></button>}
            </div>
            {editMode && activeProject.backgroundImage && <div className="background-controls"><span>参考底图</span><input aria-label="参考底图透明度" type="range" min="0.05" max="0.9" step="0.05" value={activeProject.backgroundOpacity ?? 0.35} onChange={(event) => updateProject((project) => ({ ...project, backgroundOpacity: Number(event.target.value), updatedAt: Date.now() }))} /><strong>{Math.round((activeProject.backgroundOpacity ?? 0.35) * 100)}%</strong><button type="button" onClick={() => { beginEditStroke(); updateProject((project) => ({ ...project, backgroundImage: undefined, updatedAt: Date.now() })); }}><Trash2 size={15} />移除</button></div>}
            <BeadCanvas project={activeProject} selectedColor={selectedColor} focusMode={focusMode} gridMode={gridMode} tool={tool} showCodes={showCodes} showMarks={showMarks} showHighlight={showHighlight} editDimPercent={editDimPercent} editMode={editMode} canUndo={canUndo} canRedo={canRedo} selection={selection} mouseButtons={mouseButtons} hoverCell={hoverCell} clipboardSize={patternClipboard ? { width: patternClipboard.width, height: patternClipboard.height } : null} pastePlacement={pastePlacement} tabletMode={tabletMode} touchDevice={mobileTouchDevice} beadBoardMode={beadBoardMode} eyeComfortMode={eyeComfortMode} watermark={watermark} onWatermarkChange={saveWatermark} onBeadBoardModeChange={setBeadBoardMode} onToolChange={changeTool} onToggleComplete={toggleComplete} onEditStart={beginEditStroke} onEditCell={editCell} onFillCell={fillArea} onEditEnd={() => undefined} onPickColor={(index) => { setSelectedColor(index); if (editMode) changeTool('pencil'); }} onOpenPalette={() => setQuickPaletteOpen(true)} onOpenShortcutPalette={() => setShortcutPaletteOpen(true)} onUndo={undoEdit} onRedo={redoEdit} onSelectionChange={setSelection} onHoverCellChange={setHoverCell} onPasteAt={pasteSelection} onMoveSelection={moveSelection} onCancelSelection={cancelSelection} onOpenMouseSettings={() => setMouseSettingsOpen(true)} />
          </section>
          {colorOpen && !editMode && <ColorPanel project={activeProject} selectedColor={selectedColor} focusMode={focusMode} editing={false} onSelect={(index) => { setSelectedColor(index); if (focusMode === 'all') setFocusMode('dim'); }} onFocusMode={setFocusMode} onMarkColor={markColor} />}
        </main>
      )}

      {(error || notice) && <div className={`toast canvas-toast ${error ? 'error' : 'success'}`} role="status">{error || notice}</div>}
      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
      {membershipOpen && <MembershipModal onClose={() => setMembershipOpen(false)} />}
      <FeedbackDialog />
      {activeProject && quickPaletteOpen && <QuickPaletteModal selectedColor={selectedColor} onClose={() => setQuickPaletteOpen(false)} onSelect={(index) => { setSelectedColor(index); changeTool('pencil'); setQuickPaletteOpen(false); setNotice(`已选择 MARD ${MARD_291[index].code}`); }} />}
      {activeProject && shortcutPaletteOpen && <ShortcutPaletteModal selectedColor={selectedColor} colors={shortcutPaletteColors} onChange={saveShortcutPalette} onClose={() => setShortcutPaletteOpen(false)} onSelect={(index) => { setSelectedColor(index); changeTool('pencil'); setShortcutPaletteOpen(false); setNotice(`已选择快捷颜色 MARD ${MARD_291[index].code}`); }} />}
      {activeProject && paletteConversionOpen && <PaletteConversionModal project={activeProject} onClose={() => setPaletteConversionOpen(false)} onConfirm={({ brand, name, codeMap }) => {
        updateProject((project) => ({ ...project, paletteBrand: brand, paletteName: name, paletteCodeMap: codeMap, updatedAt: Date.now() }));
        setPaletteConversionOpen(false);
        setNotice(`已切换为 ${brand} 色号标签，图纸原始颜色保持不变`);
      }} />}
      {activeProject && resizeOpen && <CanvasResizeModal width={activeProject.width} height={activeProject.height} maxSize={membershipAccess.maxCanvasSize} onClose={() => setResizeOpen(false)} onConfirm={resizeProject} />}
      {activeProject && lineAdjustOpen && <PatternLineAdjustModal project={activeProject} maxSize={membershipAccess.maxCanvasSize} onClose={() => setLineAdjustOpen(false)} onConfirm={adjustProjectLine} />}
      {activeProject && replaceOpen && <ColorReplaceModal project={activeProject} selectedColor={selectedColor} selection={selection} onClose={() => setReplaceOpen(false)} onConfirm={replaceColor} />}
      {activeProject && mouseSettingsOpen && <EditorSettingsModal value={mouseButtons} saving={mouseSettingsSaving} onClose={() => setMouseSettingsOpen(false)} onSave={(next) => { void saveMouseButtons(next); }} />}
      {activeProject && deviceOpen && <SmartBoardPanel project={activeProject} selectedColor={selectedColor} focusMode={focusMode} canAccessHardwareBeta={canAccessHardwareBeta} onClose={() => setDeviceOpen(false)} onConnectionChange={setDeviceConnected} />}
      {activeProject && pendingBackgroundCrop && <ImageCropModal purpose="background" file={pendingBackgroundCrop} width={activeProject.width} height={activeProject.height} onCancel={() => { setPendingBackgroundCrop(null); if (backgroundInput.current) backgroundInput.current.value = ''; }} onKeepOriginal={() => { void updateBackground(pendingBackgroundCrop).catch((reason) => setError(reason.message)); }} onConfirm={(next) => { void updateBackground(next).catch((reason) => setError(reason.message)); }} />}
      {pendingPatternRecognition && <PatternRecognitionModal file={pendingPatternRecognition} maxCanvasSize={membershipAccess.maxCanvasSize} shortcutColors={shortcutPaletteColors} onCancel={() => setPendingPatternRecognition(null)} onConfirm={(result, recognitionShortcutColors) => {
        const file = pendingPatternRecognition;
        setPendingPatternRecognition(null);
        void acceptRecognizedPattern(result, file, recognitionShortcutColors).catch((reason) => setError(reason instanceof Error ? reason.message : '识别图纸保存失败'));
      }} />}
      {pendingColorCalibration && <ColorCalibrationModal result={pendingColorCalibration.result} title={pendingColorCalibration.kind === 'recognized' ? '识别图纸近似色校准' : '图片转换近似色校准'} onCancel={() => setPendingColorCalibration(null)} onConfirm={(result) => {
        const pending = pendingColorCalibration;
        if (pending.kind === 'recognized') void acceptRecognizedPattern(result, pending.file);
        else void acceptGeneratedPattern(result, pending).catch((reason) => setError(reason instanceof Error ? reason.message : '图纸保存失败'));
      }} />}
      {activeProject && publishOpen && <PublishPatternModal project={activeProject} pointsEnabled={user?.pointsEnabled !== false} skipReview={Boolean(user?.permissions.canSkipGalleryReview)} onClose={() => setPublishOpen(false)} onSubmitted={() => { setPublishOpen(false); setNotice(user?.permissions.canSkipGalleryReview ? '投稿已直接发布到图纸广场。' : copy('publish.submittedNotice')); }} />}
      {activeProject && printOpen && <PrintPatternModal project={activeProject} onClose={() => setPrintOpen(false)} onPrinted={(mode) => { setPrintOpen(false); setNotice(`已打开A4${mode === 'color' ? '彩色' : '单色'}打印预览`); }} />}
      {activeProject && watermarkOpen && <WatermarkModal project={activeProject} value={watermark} onChange={saveWatermark} onEdit={openWatermarkForEdit} onClose={() => setWatermarkOpen(false)} />}
      {shareScannerOpen && <SharePatternScannerModal onClose={() => setShareScannerOpen(false)} onFound={(patternId) => { setShareScannerOpen(false); setPendingGalleryPatternId(patternId); switchScreen('gallery'); }} />}
    </div>
  );
}
